import type { Valuation } from '../../hooks/valuationFragment';
import type { PastTimeSeriesForCalculations } from '../../types';
import { roundNumberToSignificant } from '../../utils/number';
import {
  getMostRecentReportedYear,
  isMostRecentReportedYearInYearlyTimeSeries,
} from '../../utils/pastAnnualRevenueEarnings';
import { FAIR_VALUE_PRECISION } from '../../utils/schema';
import { mapToTimeSeriesForCalculations } from '../TimeseriesPlot/mappers/timeseriesMap';
import {
  calculateFairValue,
  calculateFutureEarnings,
  calculateFutureMarketCapitalisation,
  calculateFutureRevenue,
  calculateFutureSharesOutstanding,
  calculateRevenueGrowthRate,
  calculateSharePriceFutureYear,
} from './FuturePEMultipleCalculation';
import type { FuturePEMultipleStrategy } from './FuturePEMultipleContext';
import {
  calculateSharesOutstandingGrowthRate,
  getCappedSharesOutstandingGrowthRate,
  getMostRecentTotalSharesOutstanding,
  getPreviousYearSharesOutstanding,
  isValidRangeOfSharesOutstandingGrowthRate,
} from './sharesOutstanding';
import { DEFAULT_TIME_PERIOD_IN_YEARS } from './constants';
import isNil from 'lodash/isNil';

export class ViewEditNarrativeFuturePEMultipleCalculator
  implements FuturePEMultipleStrategy
{
  private _valuation: Valuation;
  private _revenueYear0: number;
  private _revenueGrowthRatePercentage: number;
  private _profitMarginPercentage: number;
  private _futurePE: number;
  private _discountRatePercentage: number;
  private _timeFrame: number;
  private _sharesOutstandingGrowthRate: number;
  private _presentSharesOutstanding: number;
  private _exchangeRate: number;

  constructor(valuation: Valuation) {
    if (!valuation) {
      throw new Error('Must set a valuation');
    }

    this._valuation = valuation;

    const timeSeries = mapToTimeSeriesForCalculations(
      this._valuation.fiscalData
    );
    const mostRecentYear = getMostRecentReportedYear(timeSeries);

    // There's a case that if the company has not reported, such as a new company,
    // it will be impossible to calculate fair value, so we need to handle this case
    // from a product perspective
    const sharesOutstanding = getMostRecentTotalSharesOutstanding({
      mostRecentReportedYear: mostRecentYear,
      pastTimeSeriesForCalculations: timeSeries,
    });

    const previousYearSharesOutstanding = getPreviousYearSharesOutstanding({
      mostRecentReportedYear: mostRecentYear,
      pastTimeSeriesForCalculations: timeSeries,
    });

    this._revenueYear0 = this.getRevenueYear0({
      mostRecentReportedYear: mostRecentYear,
      pastTimeSeriesForCalculations: timeSeries,
    });

    this._presentSharesOutstanding = sharesOutstanding;
    this._revenueGrowthRatePercentage =
      this._valuation.revenuePercentageChange ?? 0;

    this._profitMarginPercentage =
      this._valuation.profitMarginPercentageChange ?? 0;
    this._futurePE = this._valuation.futurePePercentageChange ?? 0;
    this._discountRatePercentage = this._valuation.discountRate ?? 0;
    this._timeFrame = this._valuation.timeframe ?? DEFAULT_TIME_PERIOD_IN_YEARS;

    const sharesOutstandingGrowthRate = !isNil(
      this._valuation.sharesOutstandingGrowthRate
    )
      ? this._valuation.sharesOutstandingGrowthRate
      : sharesOutstanding !== 0 && previousYearSharesOutstanding !== 0
        ? calculateSharesOutstandingGrowthRate({
            sharesOutstanding,
            previousYearSharesOutstanding,
          })
        : 0;
    this._sharesOutstandingGrowthRate =
      isValidRangeOfSharesOutstandingGrowthRate(sharesOutstandingGrowthRate)
        ? sharesOutstandingGrowthRate
        : getCappedSharesOutstandingGrowthRate(sharesOutstandingGrowthRate);

    this._exchangeRate = valuation.exchangeRate;
  }

  private get _futureRevenue() {
    return calculateFutureRevenue({
      revenueYear0: this._revenueYear0,
      revenueGrowthRatePercentage: this._revenueGrowthRatePercentage,
      timeFrame: this._timeFrame,
    });
  }

  private getRevenueYear0({
    mostRecentReportedYear,
    pastTimeSeriesForCalculations,
  }: {
    mostRecentReportedYear: string;
    pastTimeSeriesForCalculations?: PastTimeSeriesForCalculations;
  }) {
    const presentRevenue = isMostRecentReportedYearInYearlyTimeSeries({
      mostRecentReportedYear,
      pastTimeSeriesModel: pastTimeSeriesForCalculations,
    })
      ? Number(
          pastTimeSeriesForCalculations?.yearlyTimeSeries[
            mostRecentReportedYear
          ].revenue
        )
      : 0; // Assuming migration of legacy fair value and so should always have fiscal data for editing or viewing

    return presentRevenue;
  }

  public calculateFairValue() {
    const data = {
      revenueYear0: this._revenueYear0,
      revenueGrowthRate: this._revenueGrowthRatePercentage,
      profitMargin: this._profitMarginPercentage,
      futurePE: this._futurePE,
      presentSharesOutstanding: this._presentSharesOutstanding,
      discountRate: this._discountRatePercentage,
      timeFrame: this._timeFrame,
      sharesOutstandingGrowthRate: this._sharesOutstandingGrowthRate,
    };

    return roundNumberToSignificant(
      calculateFairValue(data),
      FAIR_VALUE_PRECISION
    );
  }

  public get revenueGrowthRatePercentage() {
    return this._revenueGrowthRatePercentage;
  }

  public get profitMarginPercentage() {
    return this._profitMarginPercentage;
  }

  public get futurePE() {
    return this._futurePE;
  }

  public get discountRatePercentage() {
    return this._discountRatePercentage;
  }

  public get timeFrame() {
    return this._timeFrame;
  }

  public get exchangeRate() {
    return this._exchangeRate;
  }

  public get futureSharesOutstanding() {
    return calculateFutureSharesOutstanding({
      presentSharesOutstanding: this._presentSharesOutstanding,
      sharesOutstandingGrowthRate: this._sharesOutstandingGrowthRate,
      timeFrame: this._timeFrame,
    });
  }

  public get futureEarnings() {
    return calculateFutureEarnings({
      futureRevenue: this._futureRevenue,
      profitMarginPercentage: this._profitMarginPercentage,
    });
  }

  public get futureMarketCapitalisation() {
    return calculateFutureMarketCapitalisation({
      futureEarnings: this.futureEarnings,
      futurePE: this._futurePE,
    });
  }

  public get sharesOutstandingGrowthRate() {
    return this._sharesOutstandingGrowthRate;
  }

  public get sharePriceFutureYear() {
    return calculateSharePriceFutureYear({
      futureMarketCapitalisation: this.futureMarketCapitalisation,
      futureSharesOutstanding: this.futureSharesOutstanding,
    });
  }

  public setRevenueGrowthRate(revenueGrowthRatePercentage: number): this {
    this._revenueGrowthRatePercentage = revenueGrowthRatePercentage;
    return this;
  }

  public setProfitMargin(profitMarginPercentage: number): this {
    this._profitMarginPercentage = profitMarginPercentage;
    return this;
  }

  public setFuturePE(futurePE: number): this {
    this._futurePE = futurePE;
    return this;
  }

  public setDiscountRate(discountRatePercentage: number): this {
    this._discountRatePercentage = discountRatePercentage;
    return this;
  }

  public setTimeFrame(timeFrame: number): this {
    this._timeFrame = timeFrame;
    return this;
  }

  public setFairValue(fairValue: number) {
    const {
      _timeFrame,
      _discountRatePercentage,
      _futurePE,
      _profitMarginPercentage,
      _revenueYear0,
      _presentSharesOutstanding,
      _sharesOutstandingGrowthRate,
    } = this;

    const hasSameReportingCurrencyAsListingCurrency = this._exchangeRate === 1;

    const fairValueWithExchangeRate = fairValue / this._exchangeRate;

    this._revenueGrowthRatePercentage = calculateRevenueGrowthRate({
      fairValue: hasSameReportingCurrencyAsListingCurrency
        ? fairValue
        : fairValueWithExchangeRate,
      discountRate: _discountRatePercentage,
      profitMargin: _profitMarginPercentage,
      futurePE: _futurePE,
      revenueYear0: _revenueYear0,
      timeFrame: _timeFrame,
      sharesOutstanding: _presentSharesOutstanding,
      sharesOutstandingGrowthRate: _sharesOutstandingGrowthRate,
    });

    return this;
  }

  public calculateFairValueToListingCurrency() {
    return roundNumberToSignificant(
      this.calculateFairValue() * this._exchangeRate,
      FAIR_VALUE_PRECISION
    );
  }
}
